import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Building2, Users, ChevronDown, ChevronUp, MapPin, Clock,
  CheckCircle, Circle, RefreshCw, ToggleLeft, ToggleRight,
  User, Phone, FileText, Navigation, Calendar
} from 'lucide-react';
import { API } from '../../utils/apiConfig';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

export const AdminB2BDashboardTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [toggles, setToggles] = useState({
    einsatz: true,
    billing: false,
    maps: false,
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/b2b/user/admin/dashboard?date=${selectedDate}`, { headers: getHeaders() });
      setData(res.data);
      // Auto-expand first org
      if (res.data.organizations?.length > 0 && !expandedOrg) {
        setExpandedOrg(res.data.organizations[0].id);
      }
    } catch (err) {
      console.error('B2B Dashboard load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const dutyMap = data?.duty_statuses || {};

  const getDutyBadge = (userId) => {
    const d = dutyMap[userId];
    if (!d) return <span style={styles.badgeOff}>{t('b2b.dutyOffline', 'Offline')}</span>;
    const colors = {
      dienstbeginn: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e', label: t('b2b.dutyActive', 'Im Dienst') },
      online: { bg: 'rgba(99,102,241,0.15)', color: '#818cf8', label: t('b2b.dutyOnline', 'Online') },
      pause: { bg: 'rgba(234,179,8,0.15)', color: '#eab308', label: t('b2b.dutyPause', 'Pause') },
    };
    const c = colors[d.status] || colors.online;
    return <span style={{ ...styles.badge, background: c.bg, color: c.color }}>{c.label}</span>;
  };

  const getStopStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle size={14} style={{ color: '#22c55e' }} />;
    if (status === 'arrived') return <Navigation size={14} style={{ color: '#eab308' }} />;
    return <Circle size={14} style={{ color: '#475569' }} />;
  };

  if (loading) {
    return (
      <div className="admin-tab-content" data-testid="b2b-dashboard-loading">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '2rem', color: '#818cf8' }}>
          <RefreshCw size={18} className="spin" /> {t('admin.loading', 'Laden...')}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-tab-content" data-testid="admin-b2b-dashboard-tab">
      {/* Toggle Switches */}
      <div style={styles.toggleBar} data-testid="b2b-toggle-bar">
        {[
          { key: 'einsatz', label: t('b2b.toggleEinsatz', 'B2B-Einsatzmodus') },
          { key: 'billing', label: t('b2b.toggleBilling', 'Stripe-Billing') },
          { key: 'maps', label: t('b2b.toggleMaps', 'Professional Maps') },
        ].map(tog => (
          <button
            key={tog.key}
            onClick={() => setToggles(prev => ({ ...prev, [tog.key]: !prev[tog.key] }))}
            style={styles.toggleBtn}
            data-testid={`b2b-toggle-${tog.key}`}
          >
            {toggles[tog.key]
              ? <ToggleRight size={24} style={{ color: '#22c55e' }} />
              : <ToggleLeft size={24} style={{ color: '#475569' }} />
            }
            <span style={{ color: toggles[tog.key] ? '#e0e7ff' : '#64748b' }}>{tog.label}</span>
          </button>
        ))}
      </div>

      {/* Date Picker + Refresh */}
      <div style={styles.toolbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Calendar size={16} style={{ color: '#6366f1' }} />
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            style={styles.dateInput}
            data-testid="b2b-dashboard-date"
          />
        </div>
        <button onClick={loadData} style={styles.refreshBtn} data-testid="b2b-dashboard-refresh">
          <RefreshCw size={14} /> {t('admin.actions.reload', 'Reload')}
        </button>
      </div>

      {/* Organization Accordion */}
      {(data?.organizations || []).map(org => (
        <div key={org.id} style={styles.orgCard} data-testid={`b2b-dash-org-${org.id}`}>
          <button
            onClick={() => setExpandedOrg(expandedOrg === org.id ? null : org.id)}
            style={styles.orgHeader}
            data-testid={`b2b-dash-org-toggle-${org.id}`}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Building2 size={18} style={{ color: '#818cf8' }} />
              <div>
                <div style={{ fontWeight: 600, color: '#e0e7ff', fontSize: '0.9rem' }}>{org.name}</div>
                <div style={{ fontSize: '0.7rem', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  {org.org_type} | {org.members?.length || 0} {t('b2b.members', 'Mitglieder')}
                </div>
              </div>
            </div>
            {expandedOrg === org.id ? <ChevronUp size={18} style={{ color: '#818cf8' }} /> : <ChevronDown size={18} style={{ color: '#475569' }} />}
          </button>

          {expandedOrg === org.id && (
            <div style={styles.orgBody}>
              {(org.members || []).length === 0 ? (
                <div style={{ padding: '1.5rem', color: '#475569', textAlign: 'center', fontSize: '0.8rem' }}>
                  {t('b2b.noMembers', 'Keine Mitglieder')}
                </div>
              ) : (
                (org.members || []).map(member => (
                  <div key={member.id} style={styles.memberCard} data-testid={`b2b-dash-member-${member.id}`}>
                    <button
                      onClick={() => setExpandedUser(expandedUser === member.id ? null : member.id)}
                      style={styles.memberHeader}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <Users size={15} style={{ color: '#94a3b8' }} />
                        <span style={{ fontWeight: 500, color: '#e0e7ff', fontSize: '0.85rem' }}>{member.name}</span>
                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>({member.b2b_role})</span>
                        {getDutyBadge(member.id)}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                          {member.routes?.reduce((s, r) => s + (r.stops?.length || 0), 0) || 0} Stopps
                        </span>
                        {expandedUser === member.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </button>

                    {expandedUser === member.id && (
                      <div style={styles.memberBody}>
                        {(member.routes || []).length === 0 ? (
                          <div style={{ padding: '1rem', color: '#475569', fontSize: '0.78rem' }}>
                            {t('b2b.noRoutes', 'Keine Routen')}
                          </div>
                        ) : (
                          (member.routes || []).map(route => (
                            <div key={route.id} style={styles.routeBlock}>
                              <div style={styles.routeTitle}>
                                <MapPin size={14} style={{ color: '#6366f1' }} />
                                <span>{route.route_name}</span>
                                <span style={styles.routeStatusBadge(route.status)}>{route.status}</span>
                              </div>
                              {/* STOPS TABLE */}
                              <div style={{ overflowX: 'auto' }}>
                                <table style={styles.table} data-testid={`b2b-dash-stops-${route.id}`}>
                                  <thead>
                                    <tr>
                                      <th style={styles.th}>{t('b2b.stopNr', 'Nr.')}</th>
                                      <th style={styles.th}>{t('b2b.patient', 'Auftrag / Patient')}</th>
                                      <th style={styles.th}>{t('b2b.addressArrival', 'Adresse')}</th>
                                      <th style={styles.th}>{t('b2b.departure', 'Abfahrt')}</th>
                                      <th style={styles.th}>{t('b2b.durationOnSite', 'Dauer (Min.)')}</th>
                                      <th style={styles.th}>{t('b2b.activity', 'Taetigkeit / Hinweis')}</th>
                                      <th style={styles.th}>{t('b2b.status', 'Status')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(route.stops || []).map(stop => (
                                      <tr key={stop.id} style={stop.status === 'completed' ? { opacity: 0.5 } : {}}>
                                        <td style={styles.td}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                            {getStopStatusIcon(stop.status)}
                                            <span>{stop.stop_order}</span>
                                          </div>
                                        </td>
                                        <td style={styles.td}>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontWeight: 500, color: '#e0e7ff' }}>
                                              <User size={12} /> {stop.contact_name || '—'}
                                            </span>
                                            {stop.notes && (
                                              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.68rem', color: '#64748b' }}>
                                                <Phone size={10} /> {stop.notes}
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                        <td style={{ ...styles.td, color: '#94a3b8' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                            <MapPin size={12} /> {stop.address}{stop.city ? `, ${stop.city}` : ''}
                                          </div>
                                        </td>
                                        <td style={{ ...styles.td, color: '#818cf8', whiteSpace: 'nowrap' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                            <Clock size={12} /> {stop.scheduled_time ? stop.scheduled_time.slice(0, 5) : '—'}
                                          </div>
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'center', color: '#94a3b8' }}>
                                          {stop.duration_min || 30}
                                        </td>
                                        <td style={{ ...styles.td, color: '#94a3b8' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                            <FileText size={12} /> {stop.task_description || '—'}
                                          </div>
                                        </td>
                                        <td style={styles.td}>
                                          <span style={styles.stopStatusBadge(stop.status)}>
                                            {stop.status === 'completed' ? t('b2b.stopCompleted', 'Erledigt')
                                             : stop.status === 'arrived' ? t('b2b.stopArrived', 'Angekommen')
                                             : t('b2b.stopPending', 'Ausstehend')}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const styles = {
  toggleBar: {
    display: 'flex', gap: '1rem', padding: '0.75rem 0', marginBottom: '0.75rem',
    borderBottom: '1px solid rgba(99,102,241,0.15)', flexWrap: 'wrap',
  },
  toggleBtn: {
    display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none',
    border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.8rem',
    padding: '0.35rem 0',
  },
  toolbar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem',
  },
  dateInput: {
    background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(99,102,241,0.3)',
    color: '#e0e7ff', padding: '0.35rem 0.6rem', borderRadius: '6px',
    fontFamily: 'inherit', fontSize: '0.8rem',
  },
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: '0.35rem',
    background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)',
    color: '#818cf8', padding: '0.4rem 0.75rem', borderRadius: '6px',
    cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.75rem',
  },
  orgCard: {
    background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(99,102,241,0.2)',
    borderRadius: '10px', marginBottom: '0.75rem', overflow: 'hidden',
  },
  orgHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', padding: '0.75rem 1rem', background: 'transparent',
    border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
    transition: 'background 0.2s',
  },
  orgBody: { borderTop: '1px solid rgba(99,102,241,0.12)', padding: '0.5rem' },
  memberCard: {
    background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(99,102,241,0.1)',
    borderRadius: '8px', marginBottom: '0.5rem', overflow: 'hidden',
  },
  memberHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', padding: '0.6rem 0.75rem', background: 'transparent',
    border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
    color: '#c8d6e5',
  },
  memberBody: { borderTop: '1px solid rgba(99,102,241,0.08)', padding: '0.5rem' },
  routeBlock: { marginBottom: '0.75rem' },
  routeTitle: {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.4rem 0.5rem', fontSize: '0.82rem', fontWeight: 500, color: '#e0e7ff',
  },
  routeStatusBadge: (status) => ({
    fontSize: '0.6rem', padding: '0.1rem 0.4rem', borderRadius: '3px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    background: status === 'completed' ? 'rgba(34,197,94,0.15)' : 'rgba(99,102,241,0.15)',
    color: status === 'completed' ? '#22c55e' : '#818cf8',
  }),
  badge: {
    fontSize: '0.62rem', padding: '0.1rem 0.45rem', borderRadius: '3px',
    fontWeight: 500, letterSpacing: '0.3px',
  },
  badgeOff: {
    fontSize: '0.62rem', padding: '0.1rem 0.45rem', borderRadius: '3px',
    background: 'rgba(71,85,105,0.2)', color: '#475569', letterSpacing: '0.3px',
  },
  table: {
    width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem',
  },
  th: {
    padding: '0.45rem 0.5rem', textAlign: 'left', fontSize: '0.62rem',
    textTransform: 'uppercase', letterSpacing: '0.7px', color: '#6366f1',
    background: 'rgba(99,102,241,0.05)', borderBottom: '1px solid rgba(99,102,241,0.12)',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '0.45rem 0.5rem', borderBottom: '1px solid rgba(99,102,241,0.06)',
    verticalAlign: 'middle', fontSize: '0.74rem',
  },
  stopStatusBadge: (status) => ({
    display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
    fontSize: '0.65rem', padding: '0.12rem 0.4rem', borderRadius: '3px',
    background: status === 'completed' ? 'rgba(34,197,94,0.15)'
      : status === 'arrived' ? 'rgba(234,179,8,0.15)' : 'rgba(71,85,105,0.15)',
    color: status === 'completed' ? '#22c55e'
      : status === 'arrived' ? '#eab308' : '#64748b',
  }),
};
